import { Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { Spacer, Loader } from 'theSource/components';
import { api } from 'theSource/utils';
import { PageContentLookupCode, InternalPageDto } from './types';
import { mapInternalPageData, getPageContentQueryKey } from './utils';
import {
  ActionsWrapperStyled,
  CaretLeftStyled,
  CustomActionStyled,
  LinkStyled,
} from './InternalPageStyled';
import { ThemedHeader } from 'components/ThemedHeader';

export interface InternalPageProps {
  children: JSX.Element | JSX.Element[];
  title?: string;
  description?: string;
  descriptionAsHeading?: boolean;
  lookupCode?: PageContentLookupCode;
  testId?: string;
  id?: string;
  isLoading?: boolean;
  withBackLink?: boolean;
  backLinkPath?: string | (() => void);
  CustomActionComponent?: JSX.Element;
  customActionAlignment?: string;
  blockClasses?: string;
}

export const InternalPage = ({
  children,
  title,
  description,
  descriptionAsHeading = false,
  lookupCode,
  testId = 'internal-page',
  id = '',
  isLoading,
  withBackLink = false,
  backLinkPath = '..',
  CustomActionComponent,
  customActionAlignment,
  blockClasses,
}: InternalPageProps) => {
  const { t } = useTranslation();
  const { data, isLoading: isLoadingPageContent } = useQuery<InternalPageDto>(
    getPageContentQueryKey(lookupCode),
    () => api.get('/page_content', { page_content_lookup_code: lookupCode }),
    { enabled: Boolean(lookupCode) }
  );

  if (isLoading || isLoadingPageContent) {
    return <Loader />;
  }

  const pageData = data && mapInternalPageData(data.data);
  const pageTitle = lookupCode ? pageData?.pageSubtitle : title;
  const pageDescription = lookupCode ? pageData?.pageText : description;

  return (
    <main id="mainContent">
      <div
        data-testid={testId}
        id={id}
        className={`internal-page ${blockClasses || ''}`}
      >
        {(withBackLink || CustomActionComponent) && (
          <>
            <ActionsWrapperStyled>
              {withBackLink && (
                <LinkStyled
                  value={t('common:back')}
                  path={backLinkPath}
                  internal
                  underline={false}
                  IconComponent={CaretLeftStyled}
                  testId="back-button"
                />
              )}
              {CustomActionComponent && (
                <CustomActionStyled
                  $customActionAlignment={customActionAlignment}
                >
                  {CustomActionComponent}
                </CustomActionStyled>
              )}
            </ActionsWrapperStyled>
            {(pageTitle || pageDescription) && <Spacer height={4} />}
          </>
        )}
        {pageTitle && (
          <>
            <ThemedHeader>
              <span data-testid="internal-page-title">{pageTitle}</span>
            </ThemedHeader>
            <Spacer height={2} />
          </>
        )}
        {pageDescription && (
          <>
            <Spacer height={5} />
            <Typography
              data-testid="internal-page-description"
              variant={descriptionAsHeading ? 'body1Heading' : 'body1'}
              dangerouslySetInnerHTML={{ __html: pageDescription }}
            />
          </>
        )}
        {children}
      </div>
    </main>
  );
};
